/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import {
  BarChart, Bar, LineChart, Line, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import { NPSGauge, BrandRadarChart, CHART_COLORS } from './charts';
import ChartContainer from './charts/ChartContainer';
import GlassTooltip from './charts/GlassTooltip';

interface DemographicSegment {
  _id?: string;
  age?: string;
  city?: string;
  total?: number;
  awareness_rate?: number;
  count?: number;
}

interface Demographics {
  age_segments?: DemographicSegment[];
  city_segments?: DemographicSegment[];
}

interface BrandHealthChartsProps {
  metrics?: any;
  competitors?: any[];
  demographics?: Demographics | any[];
}

// Use CHART_COLORS from design system
const COLORS = {
  primary: CHART_COLORS.primary,
  secondary: CHART_COLORS.secondary,
  warning: CHART_COLORS.warning,
  danger: CHART_COLORS.negative,
  info: CHART_COLORS.info
};

export const BrandHealthCharts: React.FC<BrandHealthChartsProps> = ({
  metrics,
  competitors,
  demographics
}) => {
  // Prepare data for awareness/trust/loyalty comparison
  const metricsData = metrics ? [
    { metric: 'Awareness', value: metrics.awareness || 0, benchmark: 50 },
    { metric: 'Trust', value: metrics.trust || 0, benchmark: 45 },
    { metric: 'Loyalty', value: metrics.loyalty || 0, benchmark: 30 },
    { metric: 'Quality', value: metrics.quality || 0, benchmark: 55 }
  ] : [];

  // NPS Gauge - using new component
  const renderNPSGauge = () => {
    if (!metrics?.nps) return null;

    // Calculate NPS breakdown (example values, adjust based on your data)
    const promoters = Math.round((metrics.nps + 100) / 2);
    const detractors = 100 - promoters;
    const passives = 0;

    return (
      <NPSGauge
        score={metrics.nps}
        promoters={promoters}
        passives={passives}
        detractors={detractors}
        title="Net Promoter Score"
      />
    );
  };

  // Metrics comparison - using radar chart
  const renderMetricsChart = () => {
    if (!metricsData.length) return null;

    return (
      <BrandRadarChart
        data={metricsData.map(m => ({
          metric: m.metric,
          value: m.value,
          competitor: m.benchmark,
          fullMark: 100
        }))}
        title="Key Metrics vs Benchmark"
        showCompetitor={true}
        brandName="Your Brand"
        competitorName="Industry Avg"
      />
    );
  };

  // Competitor comparison
  const renderCompetitorChart = () => {
    if (!competitors || competitors.length === 0) return null;

    const data = competitors.slice(0, 5).map(c => ({
      name: c.brand_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
      awareness: Math.round(c.traditional?.awareness || 0),
      trust: Math.round(c.traditional?.trust || 0),
      engagement: Math.round(c.digital?.engagement || 0)
    }));

    return (
      <ChartContainer title="Competitor Comparison" height={280}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 10, right: 20, left: 10, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="name"
              stroke="var(--text-muted)"
              tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              stroke="var(--text-muted)"
              tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              domain={[0, 100]}
            />
            <Tooltip content={<GlassTooltip />} />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            <Bar dataKey="awareness" fill={COLORS.primary} name="Awareness %" radius={[2, 2, 0, 0]} />
            <Bar dataKey="trust" fill={COLORS.secondary} name="Trust %" radius={[2, 2, 0, 0]} />
            <Bar dataKey="engagement" fill={COLORS.info} name="Engagement" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    );
  };

  // Demographics pie chart
  const renderDemographicsChart = () => {
    if (!demographics) return null;

    // Handle both old array format and new object format
    if (Array.isArray(demographics)) {
      // Old format - array of demographics
      if (demographics.length === 0) return null;
      const data = demographics.slice(0, 5).map((d, i) => ({
        name: `${d._id?.age || 'Unknown'} ${d._id?.gender || ''}`,
        value: d.count || 0
      }));

      return (
        <ChartContainer title="Demographic Distribution" height={240}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                ))}
              </Pie>
              <GlassTooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }
    
    // New format - object with age_segments and city_segments
    const { age_segments = [], city_segments = [] } = demographics as Demographics;
    
    if (age_segments.length === 0 && city_segments.length === 0) return null;
    
    return (
      <>
        {city_segments.length > 0 && (
          <ChartContainer title="Regional Awareness" height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={city_segments.slice(0, 5).map(s => ({
                  ...s,
                  awareness_rate: Math.round(s.awareness_rate || 0)
                }))}
                margin={{ top: 10, right: 20, left: 10, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="city"
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={<GlassTooltip formatter={(value: any) => `${value}%`} />}
                />
                <Bar dataKey="awareness_rate" name="Awareness" radius={[4, 4, 0, 0]}>
                  {city_segments.slice(0, 5).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.awareness_rate < 60 ? COLORS.danger : entry.awareness_rate < 70 ? COLORS.warning : COLORS.secondary}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
        {age_segments.length > 0 && (
          <ChartContainer title="Age Group Awareness" height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={age_segments.slice(0, 5).map(s => ({
                  ...s,
                  awareness_rate: Math.round(s.awareness_rate || 0)
                }))}
                margin={{ top: 10, right: 20, left: 10, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="age"
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                />
                <YAxis
                  stroke="var(--text-muted)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={<GlassTooltip formatter={(value: any) => `${value}%`} />}
                />
                <Bar dataKey="awareness_rate" name="Awareness" radius={[4, 4, 0, 0]}>
                  {age_segments.slice(0, 5).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.awareness_rate < 65 ? COLORS.danger : entry.awareness_rate < 70 ? COLORS.warning : COLORS.secondary}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {renderNPSGauge()}
      {renderMetricsChart()}
      {renderCompetitorChart()}
      {renderDemographicsChart()}
    </div>
  );
};